import { useCallback, useRef } from 'react';

/**
 * Inserting a personalization variable at the caret, shared by the two screens that offer it.
 *
 * Extracted from `TemplateTextEditor`, which had the only correct implementation, so the Campaign
 * Builder could offer the same control without a second copy of the subtlest part — caret
 * restoration after an insertion. A divergence there is invisible until an operator's *second*
 * insertion overwrites their first, which is exactly the kind of defect a duplicate quietly grows.
 *
 * Generalised over "a set of named text fields and a caret" rather than the template editor's block
 * model, because the Campaign Builder's targets are two flat campaign fields rather than named
 * copy blocks. The two callers differ only in what they call their fields.
 *
 * @param fields - Current values, keyed by field name.
 * @param onChange - Receives the whole updated map.
 * @param names - Field names in order; the first is where an insertion lands with no caret yet.
 */
export default function useVariableInsertion({ fields, onChange, names }) {
  /** Where the caret was in which field, so a variable lands there and not at the end. */
  const selection = useRef(null);

  /**
   * One stable **object** ref per field - not a callback ref.
   *
   * React accepts any object carrying a `current` property as a ref, so a lazily-created one per
   * field gives each a ref that is stable across renders without a hook per field.
   */
  const fieldRefs = useRef({});

  const refFor = useCallback((name) => {
    if (!fieldRefs.current[name]) fieldRefs.current[name] = { current: null };
    return fieldRefs.current[name];
  }, []);

  const rememberSelection = useCallback(
    (name) => (event) => {
      const target = event?.target;
      if (!target || typeof target.selectionStart !== 'number') return;
      selection.current = { name, start: target.selectionStart, end: target.selectionEnd };
    },
    []
  );

  const insertVariable = useCallback(
    (label) => {
      const token = `{${label}}`;
      const active = selection.current;
      const name = active?.name ?? names[0];
      if (!name) return;

      const current = fields[name] || '';
      // Splice at the caret, replacing any selected text. Appending instead means an operator who
      // wants a variable mid-sentence has to cut and paste it back.
      const start = active?.name === name ? active.start : current.length;
      const end = active?.name === name ? active.end : current.length;
      const next = `${current.slice(0, start)}${token}${current.slice(end)}`;

      onChange({ ...fields, [name]: next });

      // Put the caret after what was just inserted, so a second variable does not overwrite it.
      const caret = start + token.length;
      selection.current = { name, start: caret, end: caret };
      const node = refFor(name).current;
      if (node?.focus) {
        requestAnimationFrame(() => {
          node.focus();
          if (node.setSelectionRange) node.setSelectionRange(caret, caret);
        });
      }
    },
    [fields, names, onChange, refFor]
  );

  return { refFor, rememberSelection, insertVariable };
}
